import { Fragment, useId } from 'react';
import { Information } from '@carbon/react/icons';
import { FloatingTooltip } from 'components/floating-tooltip';
import { Link } from 'components/link';
import {
  REASON_SEPARATOR,
  reasonParts,
  unvaluedReasonDefinition,
  type ReasonPart,
  type UnvaluedReasonContext,
} from './definitions';
import styles from './unvalued-reason.module.scss';

interface UnvaluedReasonProps extends UnvaluedReasonContext {
  /** The reason as the server sent it. */
  reason: string | null | undefined;
  /** What to render when there is no reason. */
  fallback?: string;
  /**
   * The reason's parts, each with where it gets fixed when it has a fix (INS-12687);
   * absent → the label is plain text.
   */
  parts?: readonly ReasonPart[] | null;
}

/**
 * The parts of a reason, each part with a fix as its own link, rejoined as the server
 * joined them.
 *
 * @param {readonly ReasonPart[]} parts - The reason's parts.
 * @return {JSX.Element} The rejoined parts.
 */
function LinkedParts({ parts }: { parts: readonly ReasonPart[] }) {
  return (
    <>
      {parts.map((part, index) => (
        // reasonParts never repeats a part, so its text is its key.
        <Fragment key={part.text}>
          {index > 0 && REASON_SEPARATOR}
          {part.link ? (
            <Link
              to={part.link.href}
              title={part.link.label}
              aria-label={`${part.text}: ${part.link.label}`}
              openInNewTab
            >
              {part.text}
            </Link>
          ) : (
            part.text
          )}
        </Fragment>
      ))}
    </>
  );
}

/**
 * An unvalued reason as a user reads it: the server's label verbatim, plus an
 * information tooltip carrying the definition when the label alone does not say
 * what it means or what to do about it. Uses FloatingTooltip so the tooltip
 * escapes the table's overflow clipping.
 *
 * The definition is ALSO always present as a hidden description the trigger
 * points at (aria-describedby reads hidden content), so a screen reader announces
 * it on focus without waiting for the tooltip to mount, and browse mode does not
 * read it a second time inline. Escape closes the tooltip (FloatingTooltip) and
 * leaves focus on the trigger. FloatingTooltip wraps a plain span (asChild) — its
 * clone overwrites `aria-describedby`, so the button inside keeps its own.
 *
 * Each part of the reason with a known fix links to it, opening in a new tab, so a
 * reason listing two fixable causes reaches both. A part still names the problem, so
 * its link carries the fix's label as its title and appends it to the accessible name
 * (visible text first, label-in-name).
 *
 * @param {UnvaluedReasonProps} props - The reason, its landing verdict, fallback, and fix links.
 * @return {JSX.Element} The reason, with a definition tooltip when one applies.
 */
export function UnvaluedReason({
  reason,
  fallback = '--',
  landedWithoutFormation,
  landingInterval,
  unvaluedReasons,
  parts,
}: UnvaluedReasonProps) {
  const descriptionId = useId();
  const label = reason?.trim();
  if (!label) {
    return <>{fallback}</>;
  }
  const text = parts?.some((part) => part.link) ? (
    <LinkedParts parts={parts} />
  ) : (
    label
  );
  const verdict = { landedWithoutFormation, landingInterval };
  const definition = unvaluedReasonDefinition(label, {
    ...verdict,
    unvaluedReasons,
  });
  if (!definition) {
    return <>{text}</>;
  }
  // The reasons the tooltip defines, named on its trigger rather than the whole joined label.
  const defined = reasonParts(label, unvaluedReasons).filter((part) =>
    Boolean(
      unvaluedReasonDefinition(part, { ...verdict, unvaluedReasons: [part] })
    )
  );
  const named = defined.length > 0 ? defined : [label];
  return (
    <span className={styles.reason}>
      {/* A truncating cell drops its own title once the cell holds elements, so the
          label carries one: the full reason survives an ellipsis. */}
      <span className={styles.label} title={label}>
        {text}
      </span>
      <FloatingTooltip content={definition} placement="top" asChild>
        <span className={styles.triggerWrap}>
          <button
            type="button"
            className={styles.trigger}
            aria-label={`What "${named.join('", "')}" ${named.length > 1 ? 'mean' : 'means'}`}
            aria-describedby={descriptionId}
          >
            <Information className={styles.infoIcon} aria-hidden="true" />
          </button>
        </span>
      </FloatingTooltip>
      <span id={descriptionId} hidden>
        {definition}
      </span>
    </span>
  );
}
